import { useMemo, useState } from 'react'
import type { UserData } from '../../types'
import {
  buildSnapshot,
  earliestEntryDate,
  PERIOD_DAYS,
  snapshotRange,
  snapshotRangeLabel,
  type Period,
  type Snapshot,
} from '../../utils/analytics'
import { DoughnutChart } from '../analytics/ChartCanvas'
import PeriodToggle from '../analytics/PeriodToggle'

const fmt = (n: number) => n.toLocaleString('en')
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

function summarize(s: Snapshot, goal: UserData['goal'], offset: number): string {
  const { start, end } = snapshotRange(s.days, offset)
  const per =
    s.days === 1
      ? offset === 0
        ? 'today'
        : offset === 1
          ? 'yesterday'
          : `on ${start.format('MMM D')}`
      : offset === 0
        ? s.days === 7
          ? 'over the last 7 days'
          : s.days === 365
            ? 'over the last year'
            : 'over the last 30 days'
        : `during ${start.format('MMM D')}–${end.format('MMM D')}`
  const parts: string[] = []

  if (s.caloriesIn === 0) {
    parts.push(`No food logged ${per}.`)
  } else if (s.days === 1) {
    let sent = offset === 0 ? `Today you've eaten ${fmt(s.caloriesIn)} kcal` : `${cap(per)} you ate ${fmt(s.caloriesIn)} kcal`
    if (s.tdee != null) {
      sent += ` against your ${fmt(s.tdee)} target — a ${fmt(Math.abs(s.net))} kcal ${s.net <= 0 ? 'deficit' : 'surplus'}`
    }
    parts.push(sent + '.')
  } else {
    const avgIn = Math.round(s.caloriesIn / s.days)
    let sent = `You've averaged ${fmt(avgIn)} kcal/day ${per}`
    if (s.tdee != null) {
      const avgNet = Math.round(s.net / s.days)
      sent += ` — ${fmt(Math.abs(avgNet))} kcal/day ${avgNet <= 0 ? 'under' : 'over'} your ${fmt(s.tdee)} target`
    }
    parts.push(sent + '.')
  }

  if (s.movesCount === 0) {
    parts.push(`No moves logged ${per}.`)
  } else {
    let f = `You logged ${s.movesCount} ${s.movesCount === 1 ? 'move' : 'moves'}`
    if (s.days > 1) f += ` across ${s.activeDays} ${s.activeDays === 1 ? 'day' : 'days'}`
    if (s.activeMinutes > 0) f += ` (${fmt(s.activeMinutes)} active min)`
    parts.push(f + '.')
  }

  if (s.tdee != null && s.caloriesIn > 0 && goal) {
    const avgNet = Math.round(s.net / s.days)
    if (goal === 'lose' && avgNet < 0) parts.push('On track for your goal. 💪')
    else if (goal === 'gain' && avgNet > 0) parts.push('On track for your goal. 💪')
  }

  return parts.join(' ')
}

function Chip({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-[var(--bg-card)] px-3 py-2 text-center">
      <div className="text-sm font-bold text-[var(--text-primary)]">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">{label}</div>
    </div>
  )
}

export default function DaySummary({ data }: { data: UserData }) {
  const [period, setPeriod] = useState<Period>('day')
  const [offset, setOffset] = useState(0) // whole windows into the past; 0 = ending today
  const snap = useMemo(() => buildSnapshot(data, PERIOD_DAYS[period], offset), [data, period, offset])

  const changePeriod = (p: Period) => {
    setPeriod(p)
    setOffset(0) // a fresh period always starts at "now"
  }

  // Disable paging further back once the window already reaches the oldest entry.
  const earliest = useMemo(() => earliestEntryDate(data), [data])
  const windowStart = useMemo(() => snapshotRange(PERIOD_DAYS[period], offset).start, [period, offset])
  const canOlder = earliest != null && earliest.isBefore(windowStart)
  const rangeLabel = snapshotRangeLabel(period, offset)

  const hasEnergy = snap.caloriesIn > 0 || snap.out > 0
  const deficit = snap.net <= 0
  const segments = [
    { label: 'In', value: snap.caloriesIn, color: '#f59e0b' },
    { label: 'Out', value: snap.out, color: '#2dd4bf' },
  ].filter((s) => s.value > 0)

  return (
    <section className="card mb-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="font-semibold">Snapshot</h3>
        <PeriodToggle value={period} onChange={changePeriod} />
      </div>

      <div className="mb-3 flex items-center justify-center gap-2">
        <button
          type="button"
          aria-label="Earlier"
          onClick={() => setOffset((o) => o + 1)}
          disabled={!canOlder}
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-[var(--border)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-card)] disabled:opacity-30 disabled:hover:bg-transparent"
        >
          ‹
        </button>
        <span className="min-w-[9rem] text-center text-sm font-medium text-[var(--text-primary)]">{rangeLabel}</span>
        <button
          type="button"
          aria-label="Later"
          onClick={() => setOffset((o) => Math.max(0, o - 1))}
          disabled={offset === 0}
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-[var(--border)] text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-card)] disabled:opacity-30 disabled:hover:bg-transparent"
        >
          ›
        </button>
      </div>

      <p className="mb-4 text-sm leading-relaxed text-[var(--text-secondary)]">{summarize(snap, data.goal, offset)}</p>

      <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
        {hasEnergy && segments.length > 0 ? (
          <div className="relative w-40 shrink-0">
            {/* key by window so Chart.js remounts with fresh data instead of keeping stale arcs */}
            <DoughnutChart key={`${period}-${offset}`} segments={segments} />
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
              <div className={`text-lg font-bold ${deficit ? 'text-[var(--accent-teal)]' : 'text-[var(--accent-orange)]'}`}>
                {snap.net > 0 ? '+' : snap.net < 0 ? '−' : ''}
                {fmt(Math.abs(snap.net))}
              </div>
              <div className="text-[10px] uppercase tracking-wide text-[var(--text-secondary)]">
                {snap.net === 0 ? 'balanced' : deficit ? 'deficit' : 'surplus'}
              </div>
            </div>
          </div>
        ) : null}

        <div className="w-full flex-1">
          {hasEnergy && (
            <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-secondary)]">
              <span><span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: '#f59e0b' }} />In {fmt(snap.caloriesIn)} kcal</span>
              <span><span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ backgroundColor: '#2dd4bf' }} />Out {fmt(snap.out)} kcal {snap.outIsTdee ? '(TDEE)' : '(logged burn)'}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Chip label="Moves" value={`${snap.movesCount}`} />
            <Chip label={snap.days === 1 ? 'Active min' : 'Active days'} value={snap.days === 1 ? `${snap.activeMinutes}` : `${snap.activeDays}/${snap.days}`} />
            <Chip label="Burned" value={`${fmt(snap.burned)}`} />
            <Chip label="Protein" value={`${snap.protein}g`} />
          </div>
          {!snap.outIsTdee && (
            <p className="mt-2 text-[11px] text-[var(--text-secondary)]">
              Add your profile in Settings for a full energy-balance (TDEE) view.
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
